import {
  visibleIntakeQuestions,
  type IntakeProgramSubmission,
  type PublicIntakeProgram,
} from "@bloom/shared";
import { ProgramQuestions } from "./ProgramQuestions";
export function ProgramAssessmentFields({
  program,
  submission,
  onChange,
  productIds,
  showState = false,
}: {
  program: PublicIntakeProgram;
  submission: IntakeProgramSubmission;
  onChange: (value: IntakeProgramSubmission) => void;
  productIds?: string[];
  showState?: boolean;
}) {
  const plans = program.dosagePlans || [];
  const baseAnswers = () => {
    const ids = new Set(
      visibleIntakeQuestions(program, submission.answers, {
        path: submission.path,
      }).map((q) => q.id),
    );
    return Object.fromEntries(
      Object.entries(submission.answers).filter(([id]) => ids.has(id)),
    );
  };
  return (
    <div className="space-y-5">
      {!!plans.length && (
        <>
          <fieldset>
            <legend className="text-sm font-medium mb-2">
              Where would you like to start?
            </legend>
            <div className="grid gap-3 sm:grid-cols-2">
              {(["starter", "continuation"] as const).map((path) => (
                <button
                  key={path}
                  type="button"
                  aria-pressed={submission.path === path}
                  className={`rounded-lg border p-4 text-left ${submission.path === path ? "border-blue-600 bg-blue-50" : "bg-white"}`}
                  onClick={() => {
                    if (submission.path !== path)
                      onChange({
                        ...submission,
                        path,
                        planId: undefined,
                        answers: {},
                      });
                  }}
                >
                  {path === "starter"
                    ? "Start at the beginning"
                    : "Continue my treatment"}
                </button>
              ))}
            </div>
          </fieldset>
          {showState && (
            <label className="block text-sm font-medium">
              Shipping state
              <input
                aria-label="Shipping state"
                className="mt-2 block rounded-lg border p-3 w-full"
                maxLength={2}
                placeholder="Two-letter state code"
                value={submission.state || ""}
                onChange={(e) =>
                  onChange({
                    ...submission,
                    state: e.target.value.toUpperCase(),
                    planId: undefined,
                    answers: baseAnswers(),
                  })
                }
              />
            </label>
          )}
          {submission.entry === "direct" && (
            <label className="block text-sm font-medium">
              Selected dosage plan
              <select
                className="mt-2 w-full rounded-lg border p-3"
                value={submission.planId || ""}
                onChange={(e) =>
                  onChange({
                    ...submission,
                    planId: e.target.value || undefined,
                    answers: baseAnswers(),
                  })
                }
              >
                <option value="">Choose your dosage plan</option>
                {plans
                  .filter(
                    (p) =>
                      p.path === submission.path &&
                      (!productIds || productIds.includes(p.productId)),
                  )
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {p.doseLabel}
                    </option>
                  ))}
              </select>
            </label>
          )}
          {submission.entry === "guided" && submission.planId && (
            <div className="rounded-lg bg-blue-50 p-4">
              <p className="font-medium">
                {plans.find((p) => p.id === submission.planId)?.name}
              </p>
              <p className="text-sm">
                {plans.find((p) => p.id === submission.planId)?.doseLabel}
              </p>
              <p className="text-sm mt-1">
                Complete the questions below to confirm this option.
              </p>
            </div>
          )}
        </>
      )}
      {(!plans.length || submission.path) && (
        <ProgramQuestions
          program={program}
          context={submission}
          answers={submission.answers}
          onChange={(answers) => {
            const baseIds = new Set(
              visibleIntakeQuestions(program, submission.answers, {
                path: submission.path,
              }).map((q) => q.id),
            );
            const changedBase = [...baseIds].some(
              (id) => answers[id] !== submission.answers[id],
            );
            const resetPlan = changedBase && submission.entry === "guided";
            const ids = new Set(
              visibleIntakeQuestions(program, answers, {
                path: submission.path,
                planId: resetPlan ? undefined : submission.planId,
              }).map((q) => q.id),
            );
            onChange({
              ...submission,
              planId: resetPlan ? undefined : submission.planId,
              answers: Object.fromEntries(
                Object.entries(answers).filter(([id]) => ids.has(id)),
              ),
            });
          }}
        />
      )}
    </div>
  );
}
